/**
 * Password policy — shared server + client validator.
 *
 * Sub-agent PRE-LAUNCH-FIX (2026-08-02, FIX 4). CLIENT-GATE audit
 * flagged the pre-launch 8-char password minimum as HIGH — client
 * onboarding needs a stronger default before the first paying customer
 * lands.
 *
 * Policy locked here (both server + client validate the exact same rules
 * so an inconsistency can never leave a weak password past the gate):
 *
 *   1. Minimum 12 characters.
 *   2. At least three of four character categories:
 *        - lowercase letters       (a-z)
 *        - uppercase letters       (A-Z)
 *        - digits                  (0-9)
 *        - symbols                 (anything else)
 *      Three-of-four keeps the friction low for passphrases (e.g.
 *      "correct-horse-battery-staple" has lower+symbol, so needs one
 *      more category from upper OR digit — well within reach) while
 *      still forcing meaningful entropy variation.
 *   3. Reject the top ~200 most-common passwords via embedded list.
 *      Faster than a HaveIBeenPwned round-trip for launch; the list
 *      catches "Password123!" and its ilk that trivially satisfy the
 *      categorical rules but are still on every credential-stuffing
 *      wordlist.
 *
 * The check is case-insensitive against COMMON_PASSWORDS for match
 * (attackers stuff both `password123` and `Password123`; treat them
 * as one). The categorical + length check runs case-sensitive as
 * written above.
 *
 * A future push can layer in a zxcvbn score or a HaveIBeenPwned
 * network check without changing the public API; this module is the
 * chokepoint both `/api/signup/validate-payload` and the SPA's inline
 * validators route through.
 */

export const PASSWORD_MIN_LENGTH = 12;

/** Number of the four categories that must be present. */
export const REQUIRED_CATEGORIES = 3;

/**
 * Top common passwords. Source: SecLists Common-Credentials + the
 * publicly-published top-100 lists across the last decade. Deliberately
 * lowercased for O(1) match after normalising the candidate.
 *
 * Kept under ~200 entries so the module stays small (< 4 KB); at scale
 * the check can move to a Bloom filter or a HIBP proxy.
 */
export const COMMON_PASSWORDS: readonly string[] = Object.freeze([
  '123456', '123456789', '12345678', '1234567', '1234567890', '12345',
  'password', 'password1', 'password12', 'password123', 'password1234',
  'passw0rd', 'p@ssw0rd', 'p@ssword', 'pa55word', 'pa$$word',
  'qwerty', 'qwerty123', 'qwertyuiop', 'qwerty1234', '1qaz2wsx', '1q2w3e4r',
  'zxcvbnm', 'asdfghjkl', 'abc123', 'abcd1234', 'abcdef', 'abcdefgh',
  'letmein', 'welcome', 'welcome1', 'welcome123',
  'admin', 'admin123', 'administrator', 'root', 'toor',
  'iloveyou', 'sunshine', 'princess', 'football', 'baseball', 'basketball',
  'superman', 'batman', 'trustno1', 'starwars', 'monkey', 'dragon',
  'master', 'shadow', 'jesus', 'jesus1', 'ninja', 'mustang',
  'michael', 'jennifer', 'jordan', 'harley', 'ranger', 'hunter', 'buster',
  'thomas', 'robert', 'daniel', 'freedom', 'whatever',
  'access', 'access1', 'access123', 'login', 'login123',
  'test', 'test123', 'testing', 'testing123', 'temp', 'temp123', 'temporary',
  'changeme', 'changeit', 'default', 'guest', 'user', 'user1', 'user123',
  '111111', '000000', '654321', '696969', '112233', '121212', '131313', '123123',
  '11111111', '00000000', '87654321',
  'iloveu', 'lovely', 'flower', 'hello', 'hello123', 'hellothere',
  'newpassword', 'newpass', 'nopass', 'nopassword',
  'summer', 'winter', 'autumn', 'spring',
  'jan2024', 'feb2024', 'mar2024', 'apr2024', 'may2024', 'jun2024',
  'jul2024', 'aug2024', 'sep2024', 'oct2024', 'nov2024', 'dec2024',
  'summer2024', 'winter2024', 'spring2024', 'autumn2024',
  'summer2025', 'winter2025', 'spring2025', 'autumn2025',
  'summer2026', 'winter2026', 'spring2026', 'autumn2026',
  'olytic', 'olytic123', 'olyticpassword', 'olytic1234',
  'workspace', 'workspace1', 'workspace123',
  'company', 'company1', 'company123',
  'business', 'business1', 'business123',
  '!@#$%^&*', '!@#$%^&*(', '!@#$%^', '!qaz@wsx', '!qaz2wsx',
  'p@$$w0rd', 'p@$$w0rd1', 'p@$$w0rd123',
  'letmein123', 'letmeout', 'openup', 'sesame',
  'chocolate', 'cookies', 'pizza', 'pepper', 'ginger',
  'coffee', 'coffee123', 'coffeehouse',
  'redsox', 'yankees', 'lakers', 'cowboys',
  'metallica', 'nirvana', 'greenday',
  'batman1', 'superman1', 'spiderman', 'ironman',
  'dragon1', 'dragon123',
  'passwordpassword', 'letmeinletmein',
  'qazwsx', 'qazwsxedc', 'qazwsxedcrfv',
  'trustno1234', 'iloveyou1', 'iloveyou123',
  'purple', 'orange', 'yellow', 'silver', 'golden',
  'thunder', 'lightning', 'rainbow',
  'freedom1', 'freedom123',
  'blessed', 'blessed1',
]);

const COMMON_PASSWORDS_SET: Set<string> = new Set(
  COMMON_PASSWORDS.map((p) => p.toLowerCase()),
);

export interface PasswordValidationResult {
  ok: boolean;
  /** User-friendly reason string; empty when ok is true. */
  error?: string;
}

/**
 * Count how many of the four categories the candidate uses.
 * Exported so client-side "requirements checklist" UIs can render
 * per-category ticks without re-implementing the categorisation logic.
 */
export function countPasswordCategories(pw: string): {
  hasLower: boolean;
  hasUpper: boolean;
  hasDigit: boolean;
  hasSymbol: boolean;
  count: number;
} {
  const hasLower = /[a-z]/.test(pw);
  const hasUpper = /[A-Z]/.test(pw);
  const hasDigit = /[0-9]/.test(pw);
  // Anything that isn't a letter, digit, or whitespace — allows Unicode
  // punctuation, ASCII symbols, and non-Latin punctuation like `¡`.
  const hasSymbol = /[^A-Za-z0-9\s]/.test(pw);
  const count =
    (hasLower ? 1 : 0) +
    (hasUpper ? 1 : 0) +
    (hasDigit ? 1 : 0) +
    (hasSymbol ? 1 : 0);
  return { hasLower, hasUpper, hasDigit, hasSymbol, count };
}

/**
 * Validate a password against the shared policy. Both server and client
 * call this; the returned error string is safe to render verbatim to the
 * user (no PII, no sensitive detail — just the failing constraint).
 */
export function validatePassword(pw: string): PasswordValidationResult {
  if (typeof pw !== 'string' || pw.length === 0) {
    return { ok: false, error: 'Password is required.' };
  }
  if (pw.length < PASSWORD_MIN_LENGTH) {
    return {
      ok: false,
      error: `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`,
    };
  }
  const cats = countPasswordCategories(pw);
  if (cats.count < REQUIRED_CATEGORIES) {
    return {
      ok: false,
      error:
        `Password must include at least ${REQUIRED_CATEGORIES} of: ` +
        `lowercase letters, uppercase letters, digits, symbols.`,
    };
  }
  if (COMMON_PASSWORDS_SET.has(pw.toLowerCase())) {
    return {
      ok: false,
      error:
        'That password appears on common-password lists. Please choose something less predictable.',
    };
  }
  return { ok: true };
}

/**
 * Short human-readable summary of the policy — for hint text on password
 * inputs. Kept centralised so hint copy doesn't drift from the actual
 * rules.
 */
export const PASSWORD_POLICY_HINT =
  `At least ${PASSWORD_MIN_LENGTH} characters with ${REQUIRED_CATEGORIES} of: lowercase, uppercase, digits, symbols.`;
